import { StoreActionApi } from 'react-sweet-state';

import {
  getProgressForLessons,
  LearningProgressStatus,
  ProgressForLessons,
  saveProgressForLesson,
} from 'src/services/http/progress';
import { ProgressState } from 'src/store/progress/progressStore';
import { User } from 'src/types';
import { ContentfulSmLesson } from 'src/graphql/queries/GetSmLesson';
import { formatToISODate } from 'src/utils/date';
import { completeSectionAsync, updateLessonProgressUponLessonStart } from 'src/store/progress/utils/progressUtils';
import { sendTrackEvent } from 'src/utils/analytics';
import { getSlug } from 'src/utils/slug';
import { isStandalone } from 'src/pages/LessonPage/utils/isStandalone';

export const fetchProgressForLessons =
  (lessonIds: string[], user: User | null | undefined, courseId?: string) =>
    async ({ setState }: StoreActionApi<ProgressState>): Promise<void> => {
      if (!user) {
        return;
      }

      if (!lessonIds.length) {
        return;
      }

      setState({ isLoadingProgressForLessons: true });

      const progress = await getProgressForLessons(lessonIds, courseId);

      setState({
        progressForLessons: progress,
        isLoadingProgressForLessons: false,
      });
    };

export const completeSection =
  (section: string, lesson: ContentfulSmLesson, preview: boolean, user: User | null | undefined) =>
    async ({ setState, getState }: StoreActionApi<ProgressState>): Promise<void> => {
      const { progressForLessons } = getState();

      const currentLessonProgress = progressForLessons?.lessons.find(
        (p: ProgressForLessons['lessons'][0]) => p.lessonId === lesson.sys.id,
      );
      const currentSectionProgress = currentLessonProgress?.section.find(
        (s: { sectionId: string; status: LearningProgressStatus }) => s.sectionId === section,
      );
      const isAlreadyTracked = currentSectionProgress?.status === LearningProgressStatus.COMPLETED;
      const allLessonSectionsCompleted = currentLessonProgress?.section.every(
        (section) => section.status === LearningProgressStatus.COMPLETED,
      );
      const isTheLastUncompletedSection =
      currentLessonProgress && currentLessonProgress?.section.length + 1 === lesson.sectionListCollection.items.length;

      if (isAlreadyTracked) {
        return;
      }

      if (allLessonSectionsCompleted && isTheLastUncompletedSection) {
        sendTrackEvent(
          'lessonScreen',
          'allLessonSectionsContent',
          'completed',
          {
            lessonSlug: getSlug(lesson),
            isStandalone: isStandalone(),
          },
          user?.account_id,
        );
      }

      setState({ progressForLessons: completeSectionAsync(progressForLessons, section, lesson, user, preview) });
    };

export const startLesson =
  (lesson: ContentfulSmLesson, user: User | null | undefined) =>
    async ({ getState, setState }: StoreActionApi<ProgressState>): Promise<void> => {
      const { progressForLessons } = getState();

      const lessonProgress = progressForLessons?.lessons.find((p) => p.lessonId === lesson.sys.id);
      const isNewLessonForUser = !lessonProgress || lessonProgress.status === LearningProgressStatus.NOT_STARTED;

      if (user && progressForLessons && isNewLessonForUser) {
        await saveProgressForLesson({
          atlassianId: user.account_id,
          lesson: {
            lessonId: lesson.sys.id,
            status: LearningProgressStatus.IN_PROGRESS,
            startedDate: formatToISODate(new Date(Date.now())),
          },
        });

        const currentState = getState();
        const currentProgressForLessons = currentState.progressForLessons;

        if (!currentProgressForLessons) {
          return;
        }

        const currentLessonProgress = currentProgressForLessons.lessons.find((p) => p.lessonId === lesson.sys.id);

        if (currentLessonProgress?.status === LearningProgressStatus.IN_PROGRESS) {
          return;
        }

        setState({
          progressForLessons: updateLessonProgressUponLessonStart(currentProgressForLessons, lesson.sys.id),
        });
      }
    };
